import { NATIVE_CHANNEL, NATIVE_RECEIVE_GLOBAL } from "./constants.js";
import type {
  MessageEventLike,
  PostTarget,
  Transport,
  WindowLike,
} from "./types.js";

/** Native platform detected for the current WebView. */
export type NativePlatform = "ios" | "android";

interface NativeMessageHandler {
  postMessage(message: string): void;
}

/**
 * Non-standard globals a native WebView exposes. Property names are kept in sync
 * with {@link NATIVE_CHANNEL} / {@link NATIVE_RECEIVE_GLOBAL} and accessed through
 * those constants so the contract has a single source of truth.
 */
interface NativeGlobals {
  /** iOS WKWebView: `window.webkit.messageHandlers.<NATIVE_CHANNEL>`. */
  webkit?: {
    messageHandlers?: Record<string, NativeMessageHandler | undefined>;
  };
  /** Android WebView bridge: `window.<NATIVE_CHANNEL>` (name === NATIVE_CHANNEL). */
  rcWebComponents?: NativeMessageHandler;
  /** native -> JS entry point installed by the SDK (name === NATIVE_RECEIVE_GLOBAL). */
  __rcWebComponentsReceive?: (data: unknown) => void;
}

/**
 * Detect whether the content is running inside a native WebView that exposes the
 * RevenueCat host channel.
 *
 * - iOS if `typeof window.webkit.messageHandlers.rcWebComponents.postMessage === "function"`.
 * - Android if `typeof window.rcWebComponents.postMessage === "function"`.
 * - otherwise `null` (plain web).
 */
export function detectNativeHost(win: WindowLike | undefined): NativePlatform | null {
  if (!win) return null;
  const globals = win as unknown as NativeGlobals;
  if (
    typeof globals.webkit?.messageHandlers?.[NATIVE_CHANNEL]?.postMessage ===
    "function"
  ) {
    return "ios";
  }
  if (typeof globals[NATIVE_CHANNEL]?.postMessage === "function") {
    return "android";
  }
  return null;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * Web transport: today's behavior. Listens for `message` events on `self` and
 * posts to a target window/origin. Origin/source are surfaced on each inbound
 * event so the bridge can enforce them.
 */
export class WindowTransport implements Transport {
  readonly trustedPeer = false;

  private readonly self: WindowLike;
  private listener: ((event: MessageEventLike) => void) | null = null;

  constructor(self: WindowLike) {
    this.self = self;
  }

  subscribe(onFrame: (event: MessageEventLike) => void): void {
    if (this.listener) return;
    this.listener = (event: MessageEventLike) => {
      onFrame({ data: event.data, source: event.source, origin: event.origin });
    };
    this.self.addEventListener("message", this.listener);
  }

  post(frame: unknown, target: PostTarget): void {
    if (!target.window) return;
    target.window.postMessage(frame, target.targetOrigin);
  }

  destroy(): void {
    if (this.listener) {
      this.self.removeEventListener("message", this.listener);
      this.listener = null;
    }
  }
}

/**
 * Sentinel `source` for native inbound frames. Native has a single trusted peer,
 * so this value is never validated — it exists only to keep the normalized event
 * shape consistent and debuggable.
 */
const NATIVE_SOURCE = Symbol("rc-native-host");
const NATIVE_ORIGIN = "";

/**
 * Native transport (iOS WKWebView / Android WebView). The native host is the sole
 * trusted peer.
 *
 * - JS -> native: `postMessage(JSON.stringify(frame))` — ALWAYS a string, for
 *   Android `@JavascriptInterface` / `WebMessageListener` compatibility.
 * - native -> JS: native calls the installed global
 *   `window.__rcWebComponentsReceive(data)` with a JSON string or a parsed object.
 */
export class NativeTransport implements Transport {
  readonly trustedPeer = true;

  private readonly globals: NativeGlobals;
  private readonly platform: NativePlatform;

  constructor(win: WindowLike, platform?: NativePlatform) {
    this.globals = win as unknown as NativeGlobals;
    const detected = platform ?? detectNativeHost(win);
    if (!detected) {
      throw new Error("NativeTransport: no native host channel detected");
    }
    this.platform = detected;
  }

  subscribe(onFrame: (event: MessageEventLike) => void): void {
    this.globals[NATIVE_RECEIVE_GLOBAL] = (data: unknown) => {
      const parsed = typeof data === "string" ? safeJsonParse(data) : data;
      if (parsed === undefined) return;
      onFrame({ data: parsed, source: NATIVE_SOURCE, origin: NATIVE_ORIGIN });
    };
  }

  post(frame: unknown, _target: PostTarget): void {
    const json = JSON.stringify(frame);
    if (this.platform === "ios") {
      this.globals.webkit?.messageHandlers?.[NATIVE_CHANNEL]?.postMessage(json);
    } else {
      this.globals[NATIVE_CHANNEL]?.postMessage(json);
    }
  }

  destroy(): void {
    // Remove the installed global so native can no longer reach a dead bridge.
    try {
      delete this.globals[NATIVE_RECEIVE_GLOBAL];
    } catch {
      this.globals[NATIVE_RECEIVE_GLOBAL] = undefined;
    }
  }
}
